import React, { useEffect, useState, useMemo } from 'react';
import { Spin, Select, Modal, Button } from 'antd';
import { ExpandOutlined } from '@ant-design/icons';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer
} from 'recharts';
import dayjs from 'dayjs';
import ExchangeIcon from '../assets/icons/exchange.svg';
import '../styles/ConfrontoIMUGraph.css';

const ConfrontoIMUGraph = ({ arco, geoData, radarSelected, setSelectedHash, onFeatureSelect }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedFactors, setSelectedFactors] = useState([null, null]);
  const [roadDataOptions, setRoadDataOptions] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);

  // Street selection state (when no arco is selected)
  const [selectedStreet1, setSelectedStreet1] = useState(null);
  const [selectedStreet2, setSelectedStreet2] = useState(null);
  const [manualArco, setManualArco] = useState(null);
  const [showSelectionUI, setShowSelectionUI] = useState(false);
  const [brainChartData, setBrainChartData] = useState([]);
  const [isBrainComparison, setIsBrainComparison] = useState(false);
  const [selectedBrainMetric1, setSelectedBrainMetric1] = useState('actual');
  const [selectedBrainMetric2, setSelectedBrainMetric2] = useState('actual');

  // Brain data state
  const [brainData, setBrainData] = useState({ strade: [], varchi: [] });
  const [brainTimeseries, setBrainTimeseries] = useState({ strade: [], varchi: [] });

  // Get available streets from geoData (deduplicated with proper format)
  const availableStreets = useMemo(() => {
    if (!geoData || !geoData.features) return [];

    const streetMap = new Map();

    geoData.features.forEach(feature => {
      const id = feature.properties.UnifiedID || feature.properties.hash;
      const name = feature.properties.StreetName || '';

      if (id && !streetMap.has(id)) {
        streetMap.set(id, {
          id,
          type: 'street',
          name: name ? `${name} - (${id.substring(0, 8)}...)` : `Strada - (${id.substring(0, 8)}...)`,
          displayName: name || id,
          properties: feature.properties
        });
      }
    });

    return Array.from(streetMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [geoData]);

  // Load brain data (strade and varchi) with timeseries
  useEffect(() => {
    const loadBrainData = async () => {
      try {
        // Load strade geo and timeseries
        const [stradeGeoRes, stradeTimeseriesRes, varchiGeoRes, varchiTimeseriesRes] = await Promise.all([
          fetch('/data/strade/streets_geo.json'),
          fetch('/data/strade/timeseries_long.json'),
          fetch('/data/varchi/varchi_geo.json'),
          fetch('/data/varchi/varchi_timeseries_long.json')
        ]);

        const stradeGeo = await stradeGeoRes.json();
        const stradeTimeseries = await stradeTimeseriesRes.json();
        const varchiGeo = await varchiGeoRes.json();
        const varchiTimeseries = await varchiTimeseriesRes.json();

        // Store timeseries data
        setBrainTimeseries({
          strade: stradeTimeseries,
          varchi: varchiTimeseries
        });

        // Get unique street directions from timeseries
        const uniqueStradeIds = [...new Set(stradeTimeseries.map(s => s.street_direction))];
        // Get unique varchi marker keys from timeseries
        const uniqueVarchiIds = [...new Set(varchiTimeseries.map(v => v.marker_key))];

        setBrainData({
          strade: uniqueStradeIds.map(id => {
            const geoItem = stradeGeo.find(s => s.street_direction === id);
            return {
              id,
              type: 'brain_strada',
              name: `🧠 ${id}`,
              displayName: id,
              data: geoItem || { street_direction: id }
            };
          }),
          varchi: uniqueVarchiIds.map(id => {
            const geoItem = varchiGeo.find(v => v.marker_key === id);
            return {
              id,
              type: 'brain_varco',
              name: `🧠 ${id}`,
              displayName: id,
              data: geoItem || { marker_key: id }
            };
          })
        });
      } catch (error) {
        console.error('Error loading brain data:', error);
      }
    };

    loadBrainData();
  }, []);

  // Combined options for selection (streets + brains)
  const allOptions = useMemo(() => {
    const options = [
      ...availableStreets,
      ...brainData.strade,
      ...brainData.varchi
    ];
    return options;
  }, [availableStreets, brainData]);

  // Determine display mode - count UNIQUE roads, not total records
  const effectiveArco = manualArco || arco;
  const uniqueRoadCount = effectiveArco && effectiveArco.length > 0
    ? new Set(effectiveArco.map(item => item.UnifiedID || item.hash).filter(Boolean)).size
    : 0;

  const hasRoads = uniqueRoadCount > 0;
  const hasMultipleRoads = uniqueRoadCount >= 2;

  // Listen for addToConfronto event from map markers
  useEffect(() => {
    const handleAddToConfronto = (event) => {
      const { type, id, name } = event.detail;

      // Set the selection and trigger the brain comparison
      if (!selectedStreet1) {
        setSelectedStreet1(id);
        setShowSelectionUI(true);
      } else if (!selectedStreet2 && id !== selectedStreet1) {
        setSelectedStreet2(id);
      } else if (id !== selectedStreet2) {
        // Replace the first selection
        setSelectedStreet1(id);
        setSelectedStreet2(null);
      }
    };

    window.addEventListener('addToConfronto', handleAddToConfronto);
    return () => window.removeEventListener('addToConfronto', handleAddToConfronto);
  }, [selectedStreet1, selectedStreet2]);

  // Show selection UI when no arco is provided
  useEffect(() => {
    if (!arco || arco.length === 0) {
      setShowSelectionUI(true);
    } else {
      setShowSelectionUI(false);
      setManualArco(null);
    }
  }, [arco]);

  // Build options for road metrics based on metrics.json fields
  const streetMetricOptions = useMemo(() => [
    { value: 'velocita', label: 'Velocità Media', field: 'AvgSp', unit: 'km/h' },
    { value: 'velocita_p50', label: 'Velocità Mediana (P50)', field: 'P50sp', unit: 'km/h' },
    { value: 'tempo_percorrenza', label: 'Travel Time', field: 'AvgTt', unit: 's' },
    { value: 'flusso', label: 'Flow (VEH/15min)', field: 'Flusso_Espanso', unit: 'veicoli' }
  ], []);

  // Brain sensor metric options
  const brainMetricOptions = useMemo(() => [
    { value: 'actual', label: 'Valore Attuale', field: 'Actual', unit: 's' },
    { value: 'pred5min', label: 'Previsione 5 min', field: 'Pred5min', unit: 's' },
    { value: 'pred15min', label: 'Previsione 15 min', field: 'Pred15min', unit: 's' },
    { value: 'pred30min', label: 'Previsione 30 min', field: 'Pred30min', unit: 's' }
  ], []);

  useEffect(() => {
    setRoadDataOptions(streetMetricOptions);

    if (!selectedFactors[0] && effectiveArco && effectiveArco.length > 0) {
      const uniqueRoads = new Set(effectiveArco.map(item => item.UnifiedID || item.hash).filter(Boolean)).size;
      const isMultiple = uniqueRoads >= 2;
      setSelectedFactors([streetMetricOptions[0], isMultiple ? streetMetricOptions[0] : streetMetricOptions[1]]);
    }
  }, [effectiveArco, selectedFactors, streetMetricOptions]);

  // Determine metric options based on current selection
  const getMetricOptionsForSelection = (selectionId) => {
    if (!selectionId) return streetMetricOptions;
    const option = allOptions.find(opt => opt.id === selectionId);
    if (option && (option.type === 'brain_strada' || option.type === 'brain_varco')) {
      return brainMetricOptions;
    }
    return streetMetricOptions;
  };

  // Fetch available dates from effectiveArco data
  useEffect(() => {
    if (!effectiveArco || effectiveArco.length === 0) {
      setAvailableDates([]);
      setSelectedDate(null);
      return;
    }

    const dates = new Set();
    effectiveArco.forEach(item => {
      if (item.day_group_label) {
        const dayGroupParts = item.day_group_label.split('_')[0];
        const newFormatMatch = dayGroupParts.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (newFormatMatch) {
          dates.add(dayGroupParts);
        } else {
          const parts = dayGroupParts.split('-');
          if (parts.length === 3) {
            const day = parts[0].padStart(2, '0');
            const month = parts[1].padStart(2, '0');
            const year = parts[2];
            dates.add(`${year}-${month}-${day}`);
          }
        }
      }
    });

    const sortedDates = Array.from(dates).sort();
    setAvailableDates(sortedDates);

    if (sortedDates.length > 0) {
      setSelectedDate(sortedDates[sortedDates.length - 1]);
    }
  }, [effectiveArco]);

  // Process data when date changes
  useEffect(() => {
    if (!effectiveArco || effectiveArco.length === 0) {
      setData([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    let filteredArco = effectiveArco;

    if (selectedDate) {
      filteredArco = effectiveArco.filter(element => {
        if (element.day_group_label) {
          const dayGroupParts = element.day_group_label.split('_')[0];
          const newFormatMatch = dayGroupParts.match(/^(\d{4})-(\d{2})-(\d{2})$/);
          if (newFormatMatch) {
            return dayGroupParts === selectedDate;
          } else {
            const parts = dayGroupParts.split('-');
            if (parts.length === 3) {
              const day = parts[0].padStart(2, '0');
              const month = parts[1].padStart(2, '0');
              const year = parts[2];
              return `${year}-${month}-${day}` === selectedDate;
            }
          }
        }
        return false;
      });
    }

    const roadsByHash = {};
    filteredArco.forEach(element => {
      const hash = element.UnifiedID || element.hash;
      if (!hash) return;
      if (!roadsByHash[hash]) roadsByHash[hash] = [];
      roadsByHash[hash].push(element);
    });

    const roadHashes = Object.keys(roadsByHash);
    const road1Data = roadHashes[0] ? roadsByHash[roadHashes[0]] : [];
    const road2Data = roadHashes[1] ? roadsByHash[roadHashes[1]] : [];

    const timeSeriesMap = {};
    const fieldMappings = {
      velocita: 'AvgSp',
      velocita_p50: 'P50sp',
      tempo_percorrenza: 'AvgTt',
      flusso: 'Flusso_Espanso'
    };

    road1Data.forEach(element => {
      const timeKey = element.t_start || '00:00';
      const hourKey = element.hour || 0;
      if (!timeSeriesMap[timeKey]) {
        timeSeriesMap[timeKey] = { timestamp: timeKey, hour: hourKey };
      }
      Object.keys(fieldMappings).forEach(metricKey => {
        let value = parseFloat(element[fieldMappings[metricKey]]) || 0;
        if (metricKey === 'flusso') value = value / 3;
        timeSeriesMap[timeKey][`${metricKey}_road1`] = value;
      });
    });

    road2Data.forEach(element => {
      const timeKey = element.t_start || '00:00';
      const hourKey = element.hour || 0;
      if (!timeSeriesMap[timeKey]) {
        timeSeriesMap[timeKey] = { timestamp: timeKey, hour: hourKey };
      }
      Object.keys(fieldMappings).forEach(metricKey => {
        let value = parseFloat(element[fieldMappings[metricKey]]) || 0;
        if (metricKey === 'flusso') value = value / 3;
        timeSeriesMap[timeKey][`${metricKey}_road2`] = value;
      });
    });

    const chartData = Object.values(timeSeriesMap).sort((a, b) => {
      if (a.hour !== b.hour) return a.hour - b.hour;
      return a.timestamp.localeCompare(b.timestamp);
    });

    setData(chartData);
    setLoading(false);
  }, [selectedDate, effectiveArco]);

  // Handle street selection and highlight on map
  const handleStreetSelect = (index, value) => {
    const option = allOptions.find(opt => opt.id === value);

    if (index === 0) {
      setSelectedStreet1(value);
    } else {
      setSelectedStreet2(value);
    }

    // Highlight on map if it's a street (not brain)
    if (option && option.type === 'street' && setSelectedHash) {
      const currentSelections = [];
      if (index === 0 && value) currentSelections.push(value);
      if (index === 1 && value) currentSelections.push(value);
      if (index === 0 && selectedStreet2) currentSelections.push(selectedStreet2);
      if (index === 1 && selectedStreet1) currentSelections.push(selectedStreet1);

      // Filter to only include street types
      const streetIds = currentSelections.filter(id => {
        const opt = allOptions.find(o => o.id === id);
        return opt && opt.type === 'street';
      });

      setSelectedHash(streetIds);
    }
  };

  // Handle street selection and load data
  const handleConfirmSelection = async () => {
    if (!selectedStreet1 && !selectedStreet2) return;

    setLoading(true);

    try {
      // Determine the type of each selection
      const option1 = selectedStreet1 ? allOptions.find(opt => opt.id === selectedStreet1) : null;
      const option2 = selectedStreet2 ? allOptions.find(opt => opt.id === selectedStreet2) : null;

      const hasBrainSensor = (option1 && (option1.type === 'brain_strada' || option1.type === 'brain_varco')) ||
                             (option2 && (option2.type === 'brain_strada' || option2.type === 'brain_varco'));
      const hasStreet = (option1 && option1.type === 'street') || (option2 && option2.type === 'street');

      if (hasBrainSensor && !hasStreet) {
        // Pure brain comparison
        setIsBrainComparison(true);
        const chartData = processBrainData(option1, option2);
        setBrainChartData(chartData);
        setShowSelectionUI(false);
      } else if (hasBrainSensor && hasStreet) {
        // Mixed comparison: brain + street
        setIsBrainComparison(true);
        const chartData = await processMixedData(option1, option2);
        setBrainChartData(chartData);
        setShowSelectionUI(false);
      } else {
        // Pure street comparison
        setIsBrainComparison(false);
        const response = await fetch('/new_data/lavagna.csv');
        const csvText = await response.text();

        const lines = csvText.trim().split('\n');
        const headers = lines[0].split(',');
        const allData = [];

        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',');
          const obj = {};
          headers.forEach((header, index) => {
            const value = values[index];
            const headerName = header.trim();
            if (['day_group_label', 'UnifiedID', 't_start', 't_end'].includes(headerName)) {
              obj[headerName] = value;
            } else {
              const numValue = parseFloat(value);
              obj[headerName] = isNaN(numValue) ? value : numValue;
            }
          });
          allData.push(obj);
        }

        const filteredData = allData.filter(item => {
          const itemId = item.UnifiedID;
          return itemId === selectedStreet1 || itemId === selectedStreet2;
        });

        if (filteredData.length > 0) {
          setManualArco(filteredData);
          setShowSelectionUI(false);
        }
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }

    setLoading(false);
  };

  // Process brain sensor data for chart
  const processBrainData = (option1, option2) => {
    const timeSeriesMap = {};

    // Process first sensor
    if (option1) {
      let sensorData = [];
      if (option1.type === 'brain_strada') {
        sensorData = brainTimeseries.strade.filter(s => s.street_direction === option1.id);
      } else if (option1.type === 'brain_varco') {
        sensorData = brainTimeseries.varchi.filter(v => v.marker_key === option1.id);
      }

      sensorData.forEach(item => {
        const timeKey = item.t_idx;
        if (!timeSeriesMap[timeKey]) {
          const date = new Date(item.unix_ts * 1000);
          const hh = String(date.getHours()).padStart(2, '0');
          const mm = String(date.getMinutes()).padStart(2, '0');
          timeSeriesMap[timeKey] = {
            timestamp: `${hh}:${mm}`,
            t_idx: item.t_idx
          };
        }
        timeSeriesMap[timeKey].actual_1 = Math.round((item.Actual || 0) * 100) / 100;
        timeSeriesMap[timeKey].pred5min_1 = Math.round((item.Pred5min || 0) * 100) / 100;
        timeSeriesMap[timeKey].pred15min_1 = Math.round((item.Pred15min || 0) * 100) / 100;
        timeSeriesMap[timeKey].pred30min_1 = Math.round((item.Pred30min || 0) * 100) / 100;
      });
    }

    // Process second sensor
    if (option2) {
      let sensorData = [];
      if (option2.type === 'brain_strada') {
        sensorData = brainTimeseries.strade.filter(s => s.street_direction === option2.id);
      } else if (option2.type === 'brain_varco') {
        sensorData = brainTimeseries.varchi.filter(v => v.marker_key === option2.id);
      }

      sensorData.forEach(item => {
        const timeKey = item.t_idx;
        if (!timeSeriesMap[timeKey]) {
          const date = new Date(item.unix_ts * 1000);
          const hh = String(date.getHours()).padStart(2, '0');
          const mm = String(date.getMinutes()).padStart(2, '0');
          timeSeriesMap[timeKey] = {
            timestamp: `${hh}:${mm}`,
            t_idx: item.t_idx
          };
        }
        timeSeriesMap[timeKey].actual_2 = Math.round((item.Actual || 0) * 100) / 100;
        timeSeriesMap[timeKey].pred5min_2 = Math.round((item.Pred5min || 0) * 100) / 100;
        timeSeriesMap[timeKey].pred15min_2 = Math.round((item.Pred15min || 0) * 100) / 100;
        timeSeriesMap[timeKey].pred30min_2 = Math.round((item.Pred30min || 0) * 100) / 100;
      });
    }

    return Object.values(timeSeriesMap).sort((a, b) => a.t_idx - b.t_idx);
  };

  // Process mixed data (brain + street)
  const processMixedData = async (option1, option2) => {
    const timeSeriesMap = {};

    // Get street data first
    const streetOption = option1?.type === 'street' ? option1 : option2;
    const brainOption = option1?.type !== 'street' ? option1 : option2;

    if (streetOption) {
      const response = await fetch('/new_data/lavagna.csv');
      const csvText = await response.text();

      const lines = csvText.trim().split('\n');
      const headers = lines[0].split(',');

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',');
        const obj = {};
        headers.forEach((header, index) => {
          obj[header.trim()] = values[index];
        });

        if (obj.UnifiedID === streetOption.id) {
          const timeKey = obj.t_start || '00:00';
          if (!timeSeriesMap[timeKey]) {
            timeSeriesMap[timeKey] = { timestamp: timeKey, hour: parseInt(obj.hour) || 0 };
          }
          // Use travel time (AvgTt) as the metric for comparison with brain sensors (seconds)
          timeSeriesMap[timeKey].actual_1 = Math.round((parseFloat(obj.AvgTt) || 0) * 100) / 100;
        }
      }
    }

    // Process brain sensor
    if (brainOption) {
      let sensorData = [];
      if (brainOption.type === 'brain_strada') {
        sensorData = brainTimeseries.strade.filter(s => s.street_direction === brainOption.id);
      } else if (brainOption.type === 'brain_varco') {
        sensorData = brainTimeseries.varchi.filter(v => v.marker_key === brainOption.id);
      }

      sensorData.forEach(item => {
        const date = new Date(item.unix_ts * 1000);
        const hh = String(date.getHours()).padStart(2, '0');
        const mm = String(date.getMinutes()).padStart(2, '0');
        const timeKey = `${hh}:${mm}`;

        if (!timeSeriesMap[timeKey]) {
          timeSeriesMap[timeKey] = { timestamp: timeKey, t_idx: item.t_idx };
        }
        timeSeriesMap[timeKey].actual_2 = Math.round((item.Actual || 0) * 100) / 100;
        timeSeriesMap[timeKey].pred5min_2 = Math.round((item.Pred5min || 0) * 100) / 100;
        timeSeriesMap[timeKey].pred15min_2 = Math.round((item.Pred15min || 0) * 100) / 100;
        timeSeriesMap[timeKey].pred30min_2 = Math.round((item.Pred30min || 0) * 100) / 100;
      });
    }

    return Object.values(timeSeriesMap).sort((a, b) => a.timestamp.localeCompare(b.timestamp));
  };

  const handleFactorChange = (index, option) => {
    const newFactors = [...selectedFactors];
    newFactors[index] = option;
    setSelectedFactors(newFactors);
  };

  const getDisplayName = (id) => {
    const option = allOptions.find(opt => opt.id === id);
    return option ? option.displayName : id;
  };

  // Render street selection UI
  if (showSelectionUI && (!arco || arco.length === 0)) {
    return (
      <div className="proprieta-wrapper">
        <div className="confronto-title">
          <img src={ExchangeIcon} alt="Confronto" className="confronto-icon" />
          <h3>Confronto</h3>
        </div>

        <div className="confronto-content">
          <p className="confronto-description">
            Seleziona le strade o i sensori (🧠) da confrontare.
          </p>

          <div className="confronto-selection-section">
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <div className="dropdown-container" style={{ flex: 1, minWidth: '200px' }}>
                <label className="dropdown-label">Elemento 1</label>
                <Select
                  showSearch
                  placeholder="Seleziona strada o sensore"
                  value={selectedStreet1}
                  onChange={(value) => handleStreetSelect(0, value)}
                  style={{ width: '100%' }}
                  popupStyle={{ zIndex: 10000 }}
                  getPopupContainer={(trigger) => trigger.parentElement}
                  filterOption={(input, option) =>
                    (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  allowClear
                >
                  <Select.OptGroup label="Strade">
                    {availableStreets.map(street => (
                      <Select.Option key={street.id} value={street.id}>
                        {street.name}
                      </Select.Option>
                    ))}
                  </Select.OptGroup>
                  <Select.OptGroup label="Sensori Strade">
                    {brainData.strade.map(brain => (
                      <Select.Option key={brain.id} value={brain.id}>
                        {brain.name}
                      </Select.Option>
                    ))}
                  </Select.OptGroup>
                  <Select.OptGroup label="Sensori Varchi">
                    {brainData.varchi.map(brain => (
                      <Select.Option key={brain.id} value={brain.id}>
                        {brain.name}
                      </Select.Option>
                    ))}
                  </Select.OptGroup>
                </Select>
              </div>

              <div className="dropdown-container" style={{ flex: 1, minWidth: '200px' }}>
                <label className="dropdown-label">Elemento 2 (opzionale)</label>
                <Select
                  showSearch
                  placeholder="Seleziona strada o sensore"
                  value={selectedStreet2}
                  onChange={(value) => handleStreetSelect(1, value)}
                  style={{ width: '100%' }}
                  popupStyle={{ zIndex: 10000 }}
                  getPopupContainer={(trigger) => trigger.parentElement}
                  filterOption={(input, option) =>
                    (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  allowClear
                >
                  <Select.OptGroup label="Strade">
                    {availableStreets
                      .filter(street => street.id !== selectedStreet1)
                      .map(street => (
                        <Select.Option key={street.id} value={street.id}>
                          {street.name}
                        </Select.Option>
                      ))}
                  </Select.OptGroup>
                  <Select.OptGroup label="Sensori Strade">
                    {brainData.strade
                      .filter(brain => brain.id !== selectedStreet1)
                      .map(brain => (
                        <Select.Option key={brain.id} value={brain.id}>
                          {brain.name}
                        </Select.Option>
                      ))}
                  </Select.OptGroup>
                  <Select.OptGroup label="Sensori Varchi">
                    {brainData.varchi
                      .filter(brain => brain.id !== selectedStreet1)
                      .map(brain => (
                        <Select.Option key={brain.id} value={brain.id}>
                          {brain.name}
                        </Select.Option>
                      ))}
                  </Select.OptGroup>
                </Select>
              </div>
            </div>

            <Button
              type="primary"
              onClick={handleConfirmSelection}
              disabled={!selectedStreet1 && !selectedStreet2}
              loading={loading}
              style={{ marginTop: '20px', width: '100%' }}
            >
              Conferma Selezione
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="proprieta-wrapper">
        <div className="confronto-title">
          <img src={ExchangeIcon} alt="Confronto" className="confronto-icon" />
          <h3>Confronto</h3>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
          <Spin size="large" tip="Caricamento dati confronto..." />
        </div>
      </div>
    );
  }

  // Brain comparison render
  if (isBrainComparison && brainChartData.length > 0) {
    const sensor1Name = selectedStreet1 ? getDisplayName(selectedStreet1) : 'Sensore 1';
    const sensor2Name = selectedStreet2 ? getDisplayName(selectedStreet2) : 'Sensore 2';
    const hasTwoSensors = selectedStreet1 && selectedStreet2;

    return (
      <>
        <div className="proprieta-wrapper">
          <div className="confronto-title">
            <img src={ExchangeIcon} alt="Confronto" className="confronto-icon" />
            <h3>Confronto Sensori</h3>
            <Button
              type="link"
              size="small"
              onClick={() => {
                setShowSelectionUI(true);
                setIsBrainComparison(false);
                setBrainChartData([]);
                setSelectedStreet1(null);
                setSelectedStreet2(null);
                if (setSelectedHash) setSelectedHash([]);
              }}
              style={{ marginLeft: 'auto' }}
            >
              Cambia selezione
            </Button>
          </div>

          <div className="confronto-content">
            <p className="confronto-description">
              {hasTwoSensors
                ? `Confronto tra ${sensor1Name} e ${sensor2Name}`
                : `Dati sensore: ${sensor1Name}`}
            </p>

            <div className="confronto-dropdowns">
              <div className="dropdown-container">
                <label className="dropdown-label">
                  {hasTwoSensors ? `${sensor1Name} - Metrica` : 'Prima Metrica'}
                </label>
                <Select
                  className="confronto-select"
                  placeholder="Seleziona metrica"
                  value={selectedBrainMetric1}
                  onChange={(value) => setSelectedBrainMetric1(value)}
                  getPopupContainer={(trigger) => trigger.parentElement}
                  popupStyle={{ zIndex: 10000 }}
                  style={{ width: '100%' }}
                >
                  {brainMetricOptions.map(option => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </div>

              <div className="dropdown-container">
                <label className="dropdown-label">
                  {hasTwoSensors ? `${sensor2Name} - Metrica` : 'Seconda Metrica'}
                </label>
                <Select
                  className="confronto-select"
                  placeholder="Seleziona metrica"
                  value={selectedBrainMetric2}
                  onChange={(value) => setSelectedBrainMetric2(value)}
                  getPopupContainer={(trigger) => trigger.parentElement}
                  popupStyle={{ zIndex: 10000 }}
                  style={{ width: '100%' }}
                >
                  {brainMetricOptions.map(option => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            </div>

            <div className="confronto-chart" style={{ minHeight: '400px', width: '100%', height: '400px' }}>
              <div style={{ position: 'relative', width: '100%', height: '400px' }}>
                <button
                  onClick={() => setIsExpanded(true)}
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    width: '36px',
                    height: '36px',
                    backgroundColor: 'rgba(24, 144, 255, 0.9)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    zIndex: 100,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    transition: 'all 0.3s'
                  }}
                  title="Espandi grafico"
                >
                  <ExpandOutlined />
                </button>

                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={brainChartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="timestamp" tick={{ fontSize: 11, fill: '#666' }} stroke="#ccc" interval="preserveStartEnd" />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#666' }}
                      axisLine={{ stroke: '#e0e0e0' }}
                      label={{ value: 'Secondi', angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: '#666', textAnchor: 'middle' } }}
                    />
                    <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', color: '#333' }} />
                    <Legend wrapperStyle={{ fontSize: 12, color: '#333' }} iconType="line" />

                    <Line
                      type="monotone"
                      dataKey={`${selectedBrainMetric1}_1`}
                      stroke="#A1E430"
                      strokeWidth={2}
                      dot={false}
                      name={`${sensor1Name}: ${brainMetricOptions.find(o => o.value === selectedBrainMetric1)?.label || selectedBrainMetric1}`}
                      connectNulls
                    />
                    {hasTwoSensors ? (
                      <Line
                        type="monotone"
                        dataKey={`${selectedBrainMetric2}_2`}
                        stroke="#FF8C42"
                        strokeWidth={2}
                        dot={false}
                        name={`${sensor2Name}: ${brainMetricOptions.find(o => o.value === selectedBrainMetric2)?.label || selectedBrainMetric2}`}
                        connectNulls
                      />
                    ) : (
                      <Line
                        type="monotone"
                        dataKey={`${selectedBrainMetric2}_1`}
                        stroke="#FF8C42"
                        strokeWidth={2}
                        dot={false}
                        name={brainMetricOptions.find(o => o.value === selectedBrainMetric2)?.label || selectedBrainMetric2}
                        connectNulls
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        <Modal
          open={isExpanded}
          onCancel={() => setIsExpanded(false)}
          footer={null}
          width="95%"
          centered
          title={`Confronto - ${hasTwoSensors ? `${sensor1Name} vs ${sensor2Name}` : sensor1Name}`}
          destroyOnClose
          zIndex={9999}
          style={{ top: 20 }}
        >
          <div style={{ padding: '20px 0' }}>
            <ResponsiveContainer width="100%" height={500}>
              <LineChart data={brainChartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="timestamp" tick={{ fontSize: 11, fill: '#666' }} stroke="#ccc" interval="preserveStartEnd" />
                <YAxis
                  tick={{ fontSize: 11, fill: '#666' }}
                  axisLine={{ stroke: '#e0e0e0' }}
                  label={{ value: 'Secondi', angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: '#666', textAnchor: 'middle' } }}
                />
                <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', color: '#333' }} />
                <Legend wrapperStyle={{ fontSize: 12, color: '#333' }} iconType="line" />

                <Line
                  type="monotone"
                  dataKey={`${selectedBrainMetric1}_1`}
                  stroke="#A1E430"
                  strokeWidth={2}
                  dot={false}
                  name={`${sensor1Name}: ${brainMetricOptions.find(o => o.value === selectedBrainMetric1)?.label || selectedBrainMetric1}`}
                  connectNulls
                />
                {hasTwoSensors ? (
                  <Line
                    type="monotone"
                    dataKey={`${selectedBrainMetric2}_2`}
                    stroke="#FF8C42"
                    strokeWidth={2}
                    dot={false}
                    name={`${sensor2Name}: ${brainMetricOptions.find(o => o.value === selectedBrainMetric2)?.label || selectedBrainMetric2}`}
                    connectNulls
                  />
                ) : (
                  <Line
                    type="monotone"
                    dataKey={`${selectedBrainMetric2}_1`}
                    stroke="#FF8C42"
                    strokeWidth={2}
                    dot={false}
                    name={brainMetricOptions.find(o => o.value === selectedBrainMetric2)?.label || selectedBrainMetric2}
                    connectNulls
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Modal>
      </>
    );
  }

  if (!hasRoads && !isBrainComparison) {
    return (
      <div className="proprieta-wrapper">
        <div className="confronto-title">
          <img src={ExchangeIcon} alt="Confronto" className="confronto-icon" />
          <h3>Confronto</h3>
        </div>
        <div className="confronto-no-data">
          <p>Nessun dato disponibile per gli elementi selezionati.</p>
          <Button
            type="link"
            onClick={() => {
              setShowSelectionUI(true);
              setManualArco(null);
              setIsBrainComparison(false);
              setBrainChartData([]);
              setSelectedStreet1(null);
              setSelectedStreet2(null);
              if (setSelectedHash) setSelectedHash([]);
            }}
          >
            Seleziona altri elementi
          </Button>
        </div>
      </div>
    );
  }

  const uniqueRoadIds = [...new Set((effectiveArco || []).map(item => item.UnifiedID || item.hash).filter(Boolean))];
  const street1Name = uniqueRoadIds[0] ? getDisplayName(uniqueRoadIds[0]) : 'Elemento 1';
  const street2Name = uniqueRoadIds[1] ? getDisplayName(uniqueRoadIds[1]) : 'Elemento 2';

  return (
    <>
      <div className="proprieta-wrapper">
        <div className="confronto-title">
          <img src={ExchangeIcon} alt="Confronto" className="confronto-icon" />
          <h3>Confronto</h3>
          {manualArco && (
            <Button
              type="link"
              size="small"
              onClick={() => {
                setShowSelectionUI(true);
                setManualArco(null);
                setIsBrainComparison(false);
                setBrainChartData([]);
                setSelectedStreet1(null);
                setSelectedStreet2(null);
                if (setSelectedHash) setSelectedHash([]);
              }}
              style={{ marginLeft: 'auto' }}
            >
              Cambia selezione
            </Button>
          )}
        </div>

        <div className="confronto-content">
          <div className="confronto-date-row">
            <p className="confronto-description">
              {hasMultipleRoads
                ? `Confronto tra ${street1Name} e ${street2Name}`
                : `Confronto metriche: ${street1Name}`}
            </p>

            <Select
              placeholder="Seleziona data"
              className="confronto-date-picker"
              value={selectedDate}
              onChange={setSelectedDate}
              getPopupContainer={(trigger) => trigger.parentElement}
              popupStyle={{ zIndex: 9999 }}
              style={{ borderRadius: "20px" }}
            >
              {availableDates.map(date => (
                <Select.Option key={date} value={date}>
                  {dayjs(date).format('DD/MM/YYYY')}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div className="confronto-dropdowns">
            <div className="dropdown-container">
              <label className="dropdown-label">
                {hasMultipleRoads ? `${street1Name} - Metrica` : 'Prima Metrica'}
              </label>
              <Select
                className="confronto-select"
                placeholder="Seleziona metrica"
                value={selectedFactors[0]?.value}
                onChange={(value) => {
                  const option = roadDataOptions.find(opt => opt.value === value);
                  handleFactorChange(0, option);
                }}
                getPopupContainer={(trigger) => trigger.parentElement}
                style={{ width: '100%' }}
              >
                {roadDataOptions.map(option => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <div className="dropdown-container">
              <label className="dropdown-label">
                {hasMultipleRoads ? `${street2Name} - Metrica` : 'Seconda Metrica'}
              </label>
              <Select
                className="confronto-select"
                placeholder="Seleziona metrica"
                value={selectedFactors[1]?.value}
                onChange={(value) => {
                  const option = roadDataOptions.find(opt => opt.value === value);
                  handleFactorChange(1, option);
                }}
                getPopupContainer={(trigger) => trigger.parentElement}
                style={{ width: '100%' }}
              >
                {roadDataOptions.map(option => (
                  <Select.Option key={option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            </div>
          </div>

          <div className="confronto-chart" style={{ minHeight: '400px', width: '100%', height: '400px' }}>
            <div style={{ position: 'relative', width: '100%', height: '400px' }}>
              {data.length > 0 && selectedFactors[0] && (
                <button
                  onClick={() => setIsExpanded(true)}
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    width: '36px',
                    height: '36px',
                    backgroundColor: 'rgba(24, 144, 255, 0.9)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    zIndex: 100,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    transition: 'all 0.3s'
                  }}
                  title="Espandi grafico"
                >
                  <ExpandOutlined />
                </button>
              )}

              {data.length > 0 && selectedFactors[0] ? (
                <ResponsiveContainer width="100%" height={350}>
                  <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="timestamp" tick={{ fontSize: 11, fill: '#666' }} stroke="#ccc" interval="preserveStartEnd" />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#666' }}
                      axisLine={{ stroke: '#e0e0e0' }}
                      label={{ value: selectedFactors[0]?.unit || '', angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: '#666', textAnchor: 'middle' } }}
                    />
                    <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', color: '#333' }} />
                    <Legend wrapperStyle={{ fontSize: 12, color: '#333' }} iconType="line" />

                    <Line
                      type="monotone"
                      dataKey={`${selectedFactors[0]?.value}_road1`}
                      stroke="#A1E430"
                      strokeWidth={2}
                      dot={false}
                      name={hasMultipleRoads ? `${street1Name}: ${selectedFactors[0]?.label}` : selectedFactors[0]?.label}
                      connectNulls
                    />
                    {selectedFactors[1] && hasMultipleRoads && (
                      <Line
                        type="monotone"
                        dataKey={`${selectedFactors[1]?.value}_road2`}
                        stroke="#FF8C42"
                        strokeWidth={2}
                        dot={false}
                        name={`${street2Name}: ${selectedFactors[1]?.label}`}
                        connectNulls
                      />
                    )}
                    {selectedFactors[1] && !hasMultipleRoads && (
                      <Line
                        type="monotone"
                        dataKey={`${selectedFactors[1]?.value}_road1`}
                        stroke="#FF8C42"
                        strokeWidth={2}
                        dot={false}
                        name={selectedFactors[1]?.label}
                        connectNulls
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="no-selection">
                  <p>Seleziona almeno una metrica per visualizzare il grafico</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal
        open={isExpanded}
        onCancel={() => setIsExpanded(false)}
        footer={null}
        width="95%"
        centered
        title={`Confronto - ${hasMultipleRoads ? `${street1Name} vs ${street2Name}` : street1Name}`}
        destroyOnClose
        zIndex={9999}
        style={{ top: 20 }}
      >
        <div style={{ padding: '20px 0' }}>
          {data.length > 0 && selectedFactors[0] && (
            <ResponsiveContainer width="100%" height={500}>
              <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="timestamp" tick={{ fontSize: 11, fill: '#666' }} stroke="#ccc" interval="preserveStartEnd" />
                <YAxis
                  tick={{ fontSize: 11, fill: '#666' }}
                  axisLine={{ stroke: '#e0e0e0' }}
                  label={{ value: selectedFactors[0]?.unit || '', angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: '#666', textAnchor: 'middle' } }}
                />
                <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', color: '#333' }} />
                <Legend wrapperStyle={{ fontSize: 12, color: '#333' }} iconType="line" />

                <Line
                  type="monotone"
                  dataKey={`${selectedFactors[0]?.value}_road1`}
                  stroke="#A1E430"
                  strokeWidth={2}
                  dot={false}
                  name={hasMultipleRoads ? `${street1Name}: ${selectedFactors[0]?.label}` : selectedFactors[0]?.label}
                  connectNulls
                />
                {selectedFactors[1] && hasMultipleRoads && (
                  <Line
                    type="monotone"
                    dataKey={`${selectedFactors[1]?.value}_road2`}
                    stroke="#FF8C42"
                    strokeWidth={2}
                    dot={false}
                    name={`${street2Name}: ${selectedFactors[1]?.label}`}
                    connectNulls
                  />
                )}
                {selectedFactors[1] && !hasMultipleRoads && (
                  <Line
                    type="monotone"
                    dataKey={`${selectedFactors[1]?.value}_road1`}
                    stroke="#FF8C42"
                    strokeWidth={2}
                    dot={false}
                    name={selectedFactors[1]?.label}
                    connectNulls
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </Modal>
    </>
  );
};

export default ConfrontoIMUGraph;
